/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as CaptureActions from '../../../actions';
import { CaptureStates } from '../../../constants/CaptureStates';
import { getCaptureMode } from '../../../utils';
import { DeviceError } from '../../../constants/DeviceError';

/**
---
private: true
---
**/
function ScreenCaptureEffects({
  actions,
  captureState,
  videoBlob,
  fileName,
  requestAudioOnly,
  videoSrc,
  audioDeviceId,
  videoDeviceId,
  screenSharingType,
  screenSharingActive,
  pipEnabled,
  timeElapsed,
  error,
  experimentalScreenShareOptions,
  onChange,
  onCompleted,
  onCancel,
  onModeChange,
  onPipChanged,
  onTimeChange,
  cameraEffects,
  streamMerger,
  onError,
  hasCameraError
}) {
  useEffect(() => {
    onChange(captureState);
  }, [captureState, onChange]);
  useEffect(() => {
    onTimeChange(timeElapsed);
  }, [onTimeChange, timeElapsed]);
  useEffect(() => {
    if (captureState === CaptureStates.FINISHED) {
      onCompleted(new File([videoBlob], fileName, {
        type: videoBlob.type
      }), videoSrc);
    }
  }, [captureState, onCompleted]); // we only react to FINISHED capture state

  const prevCaptureState = useRef();
  useEffect(() => {
    if (prevCaptureState.current === CaptureStates.RECORDING && captureState === CaptureStates.FINISHED) {
      onCancel();
    }
    prevCaptureState.current = captureState;
  }, [captureState, onCancel]); // we only react to FINISHED capture state

  useEffect(() => {
    onModeChange(getCaptureMode({
      audioDeviceId,
      videoDeviceId,
      screenSharingActive
    }));
  }, [audioDeviceId, videoDeviceId, screenSharingActive, onModeChange]);
  useEffect(() => () => {
    actions.unmountCleanUp();
  }, []);
  useEffect(() => {
    actions.setScreenSharingOptions(experimentalScreenShareOptions);
  }, [experimentalScreenShareOptions]);
  useEffect(() => {
    onPipChanged({
      enabled: pipEnabled,
      shouldShow: !hasCameraError && screenSharingType === 'monitor'
    });
  }, [onPipChanged, screenSharingType, pipEnabled, hasCameraError]);
  useEffect(() => {
    streamMerger.setCameraEffects(cameraEffects);
  }, [cameraEffects, streamMerger]);
  useEffect(() => {
    onError(error);
  }, [onError, error]);
  return null;
}
ScreenCaptureEffects.propTypes = {
  experimentalScreenShareOptions: PropTypes.object,
  onChange: PropTypes.func,
  onCompleted: PropTypes.func,
  onCancel: PropTypes.func,
  onError: PropTypes.func,
  onModeChange: PropTypes.func,
  onPipChanged: PropTypes.func,
  onTimeChange: PropTypes.func,
  // props from redux store
  captureState: PropTypes.oneOf(Object.values(CaptureStates)).isRequired,
  videoBlob: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  videoSrc: PropTypes.string.isRequired,
  fileName: PropTypes.string.isRequired,
  requestAudioOnly: PropTypes.bool.isRequired,
  screenSharingActive: PropTypes.bool.isRequired,
  videoDeviceId: PropTypes.string,
  audioDeviceId: PropTypes.string,
  timeElapsed: PropTypes.number.isRequired,
  screenSharingType: PropTypes.oneOf(['window', 'tab', 'monitor', 'none']).isRequired,
  pipEnabled: PropTypes.bool.isRequired,
  cameraEffects: PropTypes.array.isRequired,
  streamMerger: PropTypes.object.isRequired,
  actions: PropTypes.shape({
    unmountCleanUp: PropTypes.func.isRequired,
    setScreenSharingOptions: PropTypes.func.isRequired
  }).isRequired,
  error: PropTypes.string,
  hasCameraError: PropTypes.bool.isRequired
};
ScreenCaptureEffects.defaultProps = {
  onChange: () => {},
  onCompleted: () => {},
  onCancel: () => {},
  onError: () => {},
  onModeChange: () => {},
  onPipChanged: () => {},
  onTimeChange: () => {},
  experimentalScreenShareOptions: {},
  videoDeviceId: null,
  audioDeviceId: null
};
const mapStateToProps = state => ({
  captureState: state.captureState,
  videoBlob: state.videoBlob,
  videoSrc: state.videoSrc,
  fileName: state.fileName,
  requestAudioOnly: state.requestAudioOnly,
  screenSharingActive: state.screenSharingActive,
  videoDeviceId: state.videoDeviceId,
  audioDeviceId: state.audioDeviceId,
  timeElapsed: state.timeElapsed,
  screenSharingType: state.screenSharingType,
  pipEnabled: state.pipEnabled,
  cameraEffects: state.cameraEffects,
  streamMerger: state.streamMerger,
  error: state.msg,
  hasCameraError: state.noSystemPermissionDevices.has(DeviceError.Webcam)
});
const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(CaptureActions, dispatch)
});
export default connect(mapStateToProps, mapDispatchToProps)(ScreenCaptureEffects);